import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, MessagesSquare, Bot } from "lucide-react";

/**
 * AddonsHint (iter78) — restrained, premium discovery card for the Overview
 * page. NO fear/loss messaging ("you missed out…"). NO implied negative service
 * ("upgrade to better"). Just states the value cleanly and lets the buyer
 * choose to explore. Classy on-brand surface — same navy treatment as the rest
 * of the dashboard.
 */
export default function AddonsHint() {
  const nav = useNavigate();
  return (
    <Card className="rounded-xl border-slate-200 shadow-soft overflow-hidden" data-testid="addons-hint">
      <CardContent className="p-0">
        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto]">
          {/* Left: title + value */}
          <div className="p-6 pr-4">
            <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-500">Connexis add-ons</div>
            <h3 className="text-[16px] font-semibold text-slate-900 mt-1 leading-snug">Compound the value of every accepted lead</h3>
            <p className="text-[12.5px] text-slate-500 mt-2 leading-relaxed max-w-xl">
              Optional, metered extras built into the platform. Pay only when they fire. Two flagship add-ons that pair perfectly:
            </p>

            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => nav("/dashboard/auto-followup")}
                className="text-left rounded-md border border-slate-200 bg-white p-3.5 hover:border-[#0F172A]/60 hover:shadow-sm transition-all group"
                data-testid="addons-hint-followup"
              >
                <div className="flex items-center gap-2">
                  <span className="inline-grid place-items-center size-7 rounded-md bg-[#0F172A] text-white">
                    <MessagesSquare className="size-3.5" />
                  </span>
                  <span className="text-[13px] font-semibold text-slate-900">Auto Follow-Up</span>
                </div>
                <p className="text-[11.5px] text-slate-500 mt-2 leading-snug">Instant SMS + email to every accepted lead. <span className="text-slate-700 font-medium">£0.05 / lead</span></p>
                <div className="mt-2 text-[10.5px] uppercase tracking-[0.1em] text-slate-700 font-semibold inline-flex items-center gap-0.5 group-hover:text-[#0055FF] transition-colors">
                  Configure <ArrowUpRight className="size-3" />
                </div>
              </button>

              <button
                onClick={() => nav("/dashboard/auto-followup#triage")}
                className="text-left rounded-md border border-slate-200 bg-white p-3.5 hover:border-[#0F172A]/60 hover:shadow-sm transition-all group"
                data-testid="addons-hint-triage"
              >
                <div className="flex items-center gap-2">
                  <span className="inline-grid place-items-center size-7 rounded-md bg-[#0F172A] text-white">
                    <Bot className="size-3.5" />
                  </span>
                  <span className="text-[13px] font-semibold text-slate-900">AI Reply Triage</span>
                </div>
                <p className="text-[11.5px] text-slate-500 mt-2 leading-snug">Claude classifies every reply, executes the right action. <span className="text-slate-700 font-medium">£0.01 / £0.03</span></p>
                <div className="mt-2 text-[10.5px] uppercase tracking-[0.1em] text-slate-700 font-semibold inline-flex items-center gap-0.5 group-hover:text-[#0055FF] transition-colors">
                  Configure <ArrowUpRight className="size-3" />
                </div>
              </button>
            </div>
          </div>

          {/* Right: navy CTA strip */}
          <button
            onClick={() => nav("/dashboard/addons")}
            className="bg-[#0F172A] text-white px-6 py-5 md:py-0 md:flex md:items-center md:gap-2 hover:bg-[#1e293b] transition-colors group text-left"
            data-testid="addons-hint-cta"
          >
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-slate-400">Catalogue</div>
              <div className="text-[13px] font-semibold mt-1 group-hover:underline underline-offset-2">See all add-ons</div>
            </div>
            <ArrowUpRight className="size-4 ml-2 shrink-0" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
}
